import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';

interface Course {
  id: string;
  courseName: string;
  term: string;
  active: boolean;
  price: number;
  grade: string;
  description: string;
  imagePath: string;
  modificationDate: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    courses: Course[];
  };
}

// Centralized request deduplication
const pendingRequests = new Map<string, Promise<any>>();

interface EnrolledCoursesProps {
  embedded?: boolean;
}

const EnrolledCourses: React.FC<EnrolledCoursesProps> = ({ embedded = false }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  
  // Helper function to construct course image URL
  const getCourseImageSrc = (course: Course) => {
    // If imagePath is null or undefined, use default image
    if (!course.imagePath) {
      return '/default-course.jpg';
    }
    
    if (course.imagePath) {
      // If it's already a full URL, use it as is
      if (course.imagePath.startsWith('http')) {
        return course.imagePath;
      }
      
      // Handle relative paths - remove leading slash to avoid double slashes
      let imagePath = course.imagePath;
      if (imagePath.startsWith('/')) {
        imagePath = imagePath.substring(1); // Remove leading slash
      }
      
      // Ensure BASE_URL ends with a slash
      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
      return `${baseUrl}${imagePath}`;
    }
    // Fallback to a default image if no image path is provided
    return '/default-course.jpg';
  };

  useEffect(() => {
    fetchCourses();
  }, [currentPage, currentUser?.id]);

  const fetchCourses = async () => {
    // Create a unique key for this request to prevent duplicates
    const requestKey = `courses_${currentUser?.id}_${currentPage}_${pageSize}`;
    
    // Check if there's already a pending request for this key
    if (pendingRequests.has(requestKey)) {
      console.log('Request already in progress, skipping duplicate call');
      return pendingRequests.get(requestKey);
    }

    const requestPromise = (async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setError('لم يتم العثور على رمز المصادقة. الرجاء تسجيل الدخول مرة أخرى.');
          return;
        }

        console.log('Fetching enrolled courses:', { userId: currentUser?.id, page: currentPage });

        const response = await fetch(
          `${BASE_URL}api/Student/Student-Enrolled-Courses?studentId=${currentUser?.id}&pagenumber=${currentPage}&pagesize=${pageSize}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              ...getTenantHeaders()
            },
          }
        );

        if (!response.ok) {
        if (response.status === 401) {
          setError('انتهت صلاحية الجلسة. الرجاء تسجيل الدخول مرة أخرى.');
          navigate('/login');
          return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ApiResponse = await response.json();
          
        if (data.success && data.data) {
            setCourses(data.data.courses);
            setTotalPages(data.data.totalPages);
          setTotalCount(data.data.totalCount);
        } else {
          setError(data.message || 'حدث خطأ أثناء جلب الدورات');
          setCourses([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('حدث خطأ أثناء جلب الدورات');
        setCourses([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    })();
    
    // Store the promise and clean it up when done
    pendingRequests.set(requestKey, requestPromise);
    
    try {
      await requestPromise;
    } finally {
      pendingRequests.delete(requestKey);
    }
  };

  const handleStartCourse = (courseId: string) => {
    navigate(`/course-player/${courseId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <div
      className={
        embedded
          ? 'px-4 py-2 sm:px-6 lg:px-8'
          : 'min-h-screen bg-slate-50 px-4 py-8 pt-20 sm:px-6 lg:px-8'
      }
      dir="rtl"
    >
      <div className="mx-auto max-w-7xl">
        {!embedded && (
          <div className="mb-8 text-center">
            <h1 className="mb-2 text-3xl font-bold text-slate-900">كورساتي</h1>
            <p className="text-slate-600">
              {totalCount > 0
                ? `عندك ${totalCount} ${totalCount === 1 ? 'كورس' : 'كورسات'} مسجّل`
                : 'اكتشف كورساتك المسجّلة وابدأ رحلة التعلم'}
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-teal-200 border-t-teal-600" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-red-600">
            {error}
          </div>
        ) : courses.length === 0 ? (
          <div className="rounded-2xl border-2 border-teal-100 bg-white px-6 py-12 text-center shadow-sm">
            <h3 className="text-lg font-bold text-slate-900">مفيش كورسات مسجّلة بعد</h3>
            <p className="mt-2 text-sm text-slate-500">سجّل في كورس جديد وابدأ التعلم</p>
            {embedded && (
              <button
                type="button"
                onClick={() => navigate('/student-portal/courses')}
                className="mt-5 inline-flex rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-700"
              >
                تصفّح الكورسات
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className="group overflow-hidden rounded-xl border-2 border-teal-100 bg-white shadow-sm transition-shadow duration-300 hover:shadow-md"
                >
                  <div className="relative h-48 bg-slate-200">
                    <img
                      src={getCourseImageSrc(course)}
                      alt={course.courseName}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.src = '/default-course.jpg';
                      }}
                    />
                    {course.term && (
                      <div className="absolute right-4 top-4 rounded-full bg-teal-600 px-3 py-1 text-sm text-white">
                        {course.term === 'First' ? 'الترم الأول' : 'الترم الثاني'}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="mb-2 text-lg font-semibold text-slate-900">{course.courseName}</h3>
                    <p className="mb-4 line-clamp-2 text-sm text-slate-600">{course.description}</p>
                    <button
                      type="button"
                      onClick={() => handleStartCourse(course.id)}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-white transition-colors hover:bg-teal-700"
                    >
                      <span>بدء الدورة</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold ${
                    currentPage === 1
                      ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                      : 'border-2 border-teal-100 bg-white text-slate-700 hover:bg-teal-50'
                  }`}
                >
                  السابق
                </button>
                <span className="rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white">
                  {currentPage} من {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold ${
                    currentPage === totalPages
                      ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                      : 'border-2 border-teal-100 bg-white text-slate-700 hover:bg-teal-50'
                  }`}
                >
                  التالي
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EnrolledCourses; 